import dayjs from '~/helpers/dayjsHelper';

export const formats = {
  DATE: 'MMM D',
  LONG_DATE: 'MMM D, YYYY',
  CHAT_DATE: 'ddd, MMMM D',
  DATETIME: 'MMM D YYYY, h:mm a z',
  US_DATE: 'MM/DD/YYYY',
  SHORT_US_DATETIME: 'M/D h:mm a',
  SIGNATURE_DATE: 'MMM D, YYYY | h:mm a z',
  DATETIME_PIPE_LOCAL: 'MMM D, YYYY | h:mm a',
  TRACKING_DATE: 'MMM D, YYYY h:mm A',
  MONTH: 'MMMM',
  HOUR: 'HH:mm',
  FULL_DAY: 'dddd',
  SHORT_DATE_FORMAT: 'DD MMM, YYYY',
  APPOINTMENT_TIME_FORMAT: 'h:mm a z',
  MEDIUM_DATE: 'MMM D, YYYY',
  DATE_TIME_REPORT: 'YYYYMMDD',
}

export const initTimezone = zone => dayjs.tz.setDefault(zone);

export const formatDate = (date, format) => dayjs(date).format(format);

export const formatUtcDate = (date, format) => dayjs.utc(date).format(format);

export const formatTimestamp = (timestamp, format) => dayjs.unix(timestamp).format(format);

export const formatTimestampTz = (timestamp, format, timezone) => {
  if (!isValidTimezone(timezone)) return 'Unknown timezone';
  return dayjs.unix(timestamp).tz(timezone).format(format);
}

export const timestamp = date => Math.floor((date || Date.now()) / 1000);

export const dateFromTimestamp = timestamp => new Date(timestamp * 1000);

// receives a date as INT and returns an ISO string
// for NULL timestamp, returns an empty string
export const isoFromTimestamp = timestamp => timestamp ? dayjs.unix(timestamp).toISOString() : "";

// receives a date as STRING and returns an INT or NULL
export const timestampFromIso = date => date ? Math.floor((new Date(date).getTime()) / 1000) : null;

export const timeSince = date => formatTimeInterval(timestamp() - date);

export const formatTimeInterval = seconds => {

  let interval = seconds / 31536000;

  if (interval > 1) {
    if (interval < 2) return "1 year";
    return Math.floor(interval) + " years";
  }

  interval = seconds / 2592000;

  if (interval > 1) {
    if (interval < 2) return "1 month";
    return Math.floor(interval) + " months";
  }

  interval = seconds / 86400;

  if (interval > 1) {
    if (interval < 2) return "1 day";
    return Math.floor(interval) + " days";
  }

  interval = seconds / 3600;

  if (interval > 1) {
    if (interval < 2) return "1 hour";
    return Math.floor(interval) + " hours";
  }

  interval = seconds / 60;

  if (interval > 1) {
    if (interval < 2) return "1 minute";
    return Math.floor(interval) + " minutes";
  }

  return Math.floor(seconds) === 0 ? "now" : (Math.floor(seconds) === 1 ? "1 second" : Math.floor(seconds) + " seconds");
}

export const timeAgo = date => {
  const timeElapsed = timeSince(date);
  if (timeElapsed != 'now') {
    return `${timeElapsed} ago`;
  }
  return timeElapsed;
}

// compares two timestamps and returns true if they represent the same date
// if only 1 arg is received, compares it to the current date
export const isSameDate = (ts1, ts2 = timestamp(new Date())) => {
  return dayjs(dateFromTimestamp(ts1)).isSame(dateFromTimestamp(ts2), "day");
}

export const isPast7days = ts => {
  return dayjs(dateFromTimestamp(ts)).isAfter(dayjs().subtract(7, 'd'))
}

export const getMessageDateFormat = ts => {
  if (isSameDate(ts)) return formats.HOUR;
  if (isPast7days(ts)) return formats.FULL_DAY;
  return formats.US_DATE;
}

// receives the time in minutes
export const formatChartTimeLabel = maxTime => time => {

  // if time is 0, omit unit name
  if (time === 0) return "0";

  // if max time is less than 3 min, display all labels in seconds
  if (maxTime < 3) return Math.floor(time * 60) + " s";

  // time is less than 3 h
  if (time < 180) return Math.floor(time) + " min";
  // time is less than 3 d
  else if (time < 4320) return Math.floor(time / 60) + " h";
  // time is greater than 3 d
  else return Math.floor(time / 60 / 24) + " d";
}


// receives a Date and returns an object with the date components as keys
export const extractDateComponents = date => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();

  return { year, month, day, hours, minutes };
}

// receives a Date and changes it's time zone to the user time zone, while keeping the values for year, month, day, hours and minutes
export const localDateToTimezoneTs = date => {
  // extract the values selected by the user
  const { year, month, day, hours, minutes } = extractDateComponents(date);

  // dayjs.tz() only accepts strings (not component objects); default zone comes from initTimezone
  const pad = n => String(n).padStart(2, '0');
  const dateStr = `${year}-${pad(month + 1)}-${pad(day)} ${pad(hours)}:${pad(minutes)}`;
  return dayjs.tz(dateStr).unix();
};

// receives a dayjs instance and returns an object with the date components as keys
export const extractDayjsComponents = d => {
  const year = d.year();
  const month = d.month();
  const day = d.date();
  const hours = d.hour();
  const minutes = d.minute();

  return { year, month, day, hours, minutes };
};

// receives a unix timestamp and changes it's time zone to the local (browser) time zone, while keeping the values for year, month, day, hours and minutes
// returns a Date
export const timezoneTsToLocalDate = ts => {
  // create dayjs instance from unix timestamp
  const selectedDayjs = dayjs.unix(ts);

  // extract the values
  const { year, month, day, hours, minutes } = extractDayjsComponents(selectedDayjs);

  // the new date will be initialized with the local time zone
  return new Date(year, month, day, hours, minutes, 0, 0);
};

export const getStartOfPrevMonth = (date = new Date()) => {
  const newDate = getEndOfPrevMonth(date);
  newDate.setDate(1); // 1 will result in the first day of the month
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

export const getEndOfPrevMonth = (date = new Date()) => {
  date.setDate(0); // 0 will result in the last day of the previous month
  date.setHours(0, 0, 0, 0);
  return date;
};

export const getMonthsInRange = (startTs, endTs) => {
  if (!startTs || !endTs) return []
  // this is a temporary fix
  // all dates should be in the timezone set by the user
  const startDate = dayjs(dateFromTimestamp(startTs)).local();
  const endDate = dayjs(dateFromTimestamp(endTs)).local();

  const result = [];

  // add double digits for month, so that Date object can be created from string
  let format = "YYYY-MM";

  // if range is included in one month, return it
  if (startDate.year() === endDate.year() && startDate.month() === endDate.month()) {
    return [startDate.format(format)];
  }

  // start from begining of month — dayjs is immutable so we reassign on add
  let startMonth = dayjs(dateFromTimestamp(startTs)).startOf('month').local();
  while (startMonth.isBefore(endDate)) {
    result.push(startMonth.format(format));
    startMonth = startMonth.add(1, 'month');
  }

  return result;
};

export const getMonthName = (dateStr, isSameYear) => {
  const date = dayjs.tz(dateStr, 'YYYY-MM', dayjs.tz.guess());

  return date.format(isSameYear ? 'MMMM' : 'MMMM YYYY');
};

export const getStartOfPrevMonthTs = () => dayjs().subtract(1, "months").startOf("month").unix();

export const getEndOfPrevMonthTs = () => dayjs().subtract(1, "months").endOf("month").unix();

export const getStartOfMonthString = () => dayjs().startOf("month").format(formats.MEDIUM_DATE);

export const getEndOfMonthString = () => dayjs().endOf("month").format(formats.MEDIUM_DATE);

export const getPassedDuration = (ts1, ts2) => {
  // Calculate the duration between the two timestamps
  const duration = dayjs.duration(dayjs(ts2 * 1000).diff(dayjs(ts1 * 1000)));

  // Extract days, hours, and minutes
  const days = Math.floor(duration.asDays());
  const hours = Math.floor(duration.asHours() % 24);
  const minutes = Math.floor(duration.asMinutes() % 60);

  return { days, hours, minutes }
}

export const isValidTimezone = (timezone) => {
  if (!timezone || typeof timezone !== 'string') return false;

  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone })
    return true;
  } catch (_error) {
    return false;
  }
}
